import { type EcoleOffer, VERSANT_LIMITES } from "@shared/ecole.types";
import { jourCalendaire } from "@shared/espace.types";
import { Briefcase, CalendarDays, Send, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Badge } from "@/components/ds/Badge";
import { Button } from "@/components/ds/Button";
import { Card, CardHeader, ReadOnlyBlock } from "@/components/ds/Card";
import { EmptyState } from "@/components/ds/EmptyState";
import { DateInput, GestureError, TextAreaInput } from "@/components/ds/Fields";
import { FilterChip } from "@/components/ds/FilterChip";
import { LinkButton } from "@/components/ds/LinkButton";
import { formatDate, relativeDate } from "@/espace/labels";
import { A_TRAITER, formatJour, OFFER_STATUS_ECOLE } from "./labels";
import { type Echec, useGeste } from "./useActivite";

/**
 * Les offres déposées chez l'école, et ce qu'elle en fait.
 *
 * L'écran s'ouvre sur « À traiter » : c'est la seule liste qui attend un geste. Le
 * compte se lit sur le filtre, pas dans une phrase sous le titre. Une offre en
 * attente porte ses deux décisions sur la carte même, sans écran de plus, parce
 * que trancher est le geste le plus fréquent et qu'il tient en une date ou une
 * phrase.
 */

const FILTRES = [
	{ key: "a_traiter", label: "À traiter" },
	{ key: "validee", label: "Publiées" },
	{ key: "archivee", label: "Terminées" },
	{ key: "refusee", label: "Non retenues" },
	{ key: "all", label: "Toutes" },
] as const;

type Filtre = (typeof FILTRES)[number]["key"];

function dans(filtre: Filtre, offer: EcoleOffer): boolean {
	if (filtre === "all") return true;
	if (filtre === "a_traiter") return A_TRAITER.includes(offer.status);
	return offer.status === filtre;
}

export type OffresActions = {
	publier: (offerId: string, expiresOn: string) => Promise<Echec>;
	refuser: (offerId: string, motif: string) => Promise<Echec>;
	changerDate: (offerId: string, expiresOn: string) => Promise<Echec>;
};

export function OffresPage({ offers, actions }: { offers: EcoleOffer[]; actions: OffresActions }) {
	const aTraiter = offers.filter((offer) => A_TRAITER.includes(offer.status)).length;
	const [choisi, setFiltre] = useState<Filtre>(aTraiter > 0 ? "a_traiter" : "all");
	// Le filtre choisi qui se vide (la dernière offre à traiter vient d'être publiée)
	// disparaît de la barre : l'écran retombe alors sur « Toutes », jamais sur une
	// liste vide sans filtre sélectionné.
	const filtre: Filtre = offers.some((offer) => dans(choisi, offer)) ? choisi : "all";

	if (offers.length === 0) {
		// Le titre de page reste au-dessus de l'état vide : sans lui, l'écran n'avait
		// plus de h1 et ne disait pas où l'on est, à l'œil comme au lecteur d'écran.
		return (
			<div className="flex flex-col gap-5">
				<h1 className="t-h1 text-mg-heading">Offres reçues</h1>
				<Card padded={false}>
					<EmptyState
						icon={<Briefcase className="size-5" aria-hidden="true" />}
						title="Aucune offre reçue"
						body="Envoyez l'adresse de votre page de dépôt à vos entreprises. Leurs offres arrivent ici."
						action={<LinkButton to="/ecole/page">Voir ma page de dépôt</LinkButton>}
					/>
				</Card>
			</div>
		);
	}

	const shown = offers.filter((offer) => dans(filtre, offer));

	return (
		<div className="flex flex-col gap-5">
			<h1 className="t-h1 text-mg-heading">Offres reçues</h1>

			<div className="flex flex-wrap gap-2">
				{FILTRES.map((entry) => {
					const count = offers.filter((offer) => dans(entry.key, offer)).length;
					if (count === 0 && entry.key !== "all") return null;
					return (
						<FilterChip
							key={entry.key}
							selected={filtre === entry.key}
							count={count}
							onClick={() => setFiltre(entry.key)}
						>
							{entry.label}
						</FilterChip>
					);
				})}
			</div>

			<ul className="flex flex-col gap-4">
				{shown.map((offer) => (
					<li key={offer.id}>
						<OffreCard offer={offer} actions={actions} />
					</li>
				))}
			</ul>
		</div>
	);
}

function OffreCard({ offer, actions }: { offer: EcoleOffer; actions: OffresActions }) {
	const statut = OFFER_STATUS_ECOLE[offer.status] ?? OFFER_STATUS_ECOLE.nouveau;
	const faits = [
		offer.location,
		offer.contract,
		offer.startsOn ? `Début le ${formatJour(offer.startsOn)}` : null,
		offer.headcount && offer.headcount > 1 ? `${offer.headcount} postes` : null,
	].filter(Boolean);

	return (
		<Card padded={false}>
			<CardHeader
				title={offer.job}
				subtitle={[offer.companyName, `reçue ${relativeDate(offer.createdAt)}`]
					.filter(Boolean)
					.join(" · ")}
				action={statut ? <Badge tone={statut.tone}>{statut.label}</Badge> : undefined}
			/>
			<div className="flex flex-col gap-4 p-6">
				{faits.length > 0 ? <p className="t-body text-mg-body">{faits.join(" · ")}</p> : null}

				{offer.description ? (
					<details>
						{/* `py-3` : la ligne fait 44px sous `md`, la cible d'un pouce. Le
						    libellé seul en faisait 18. */}
						<summary className="t-label -my-3 cursor-pointer py-3 text-mg-action md:-my-1 md:py-1">
							Lire l'annonce
						</summary>
						<p className="t-body mt-2 whitespace-pre-line text-mg-body">{offer.description}</p>
					</details>
				) : null}

				<div className="grid gap-4 md:grid-cols-2">
					<ReadOnlyBlock label="Contact">
						<span className="block">{offer.contact.fullName ?? offer.contact.email}</span>
						{offer.contact.fullName ? (
							<a href={`mailto:${offer.contact.email}`} className="break-all text-mg-action">
								{offer.contact.email}
							</a>
						) : null}
						{offer.contact.phone ? <span className="block">{offer.contact.phone}</span> : null}
					</ReadOnlyBlock>
					{offer.status === "refusee" && offer.schoolNote ? (
						<ReadOnlyBlock label="Motif envoyé">{offer.schoolNote}</ReadOnlyBlock>
					) : null}
				</div>

				{A_TRAITER.includes(offer.status) ? <Trancher offer={offer} actions={actions} /> : null}
				{offer.status === "validee" || offer.status === "archivee" ? (
					<EnLigne offer={offer} actions={actions} />
				) : null}
			</div>
		</Card>
	);
}

/**
 * La date limite d'une offre, et le bouton qui la fait partir : la même rangée aux
 * deux endroits où elle se pose.
 *
 * Le champ est BORNÉ (`md:w-64`) : il prenait toute la place restante, soit 690px
 * pour une date, et repoussait « Publier » au bord de la carte. Les boutons se
 * posent à côté, alignés sur le champ et pas sur son libellé (`md:mt-6`, la
 * hauteur du libellé) ; l'aide reste sous le champ. Sous `md`, tout s'empile.
 */
function RangeeDate({
	label,
	hint,
	error,
	value,
	onChange,
	children,
}: {
	label: string;
	hint?: string;
	error?: string | null;
	value: string;
	onChange: (value: string) => void;
	children: ReactNode;
}) {
	return (
		<div className="flex flex-col gap-3 md:flex-row md:items-start">
			<div className="w-full md:w-64">
				<DateInput
					label={label}
					hint={hint}
					error={error}
					required
					min={jourCalendaire()}
					value={value}
					onChange={onChange}
				/>
			</div>
			<div className="flex flex-wrap gap-2 md:mt-6">{children}</div>
		</div>
	);
}

/**
 * Publier ou refuser. Les deux décisions sont visibles, mais une seule s'ouvre à la
 * fois : la date pour publier, le motif pour refuser. Rien ne part sans l'un des deux.
 *
 * Les boutons restent ACTIFS. Grisés tant que la date manquait, ils ne disaient pas
 * pourquoi on ne pouvait pas publier ; c'est l'erreur sous le champ, au clic, qui le
 * dit maintenant.
 */
function Trancher({ offer, actions }: { offer: EcoleOffer; actions: OffresActions }) {
	const [mode, setMode] = useState<"publier" | "refuser">("publier");
	const [date, setDate] = useState("");
	const [motif, setMotif] = useState("");
	const [manque, setManque] = useState(false);
	const geste = useGeste();

	function publier() {
		if (!date) {
			setManque(true);
			return;
		}
		void geste.lancer(() => actions.publier(offer.id, date));
	}

	function refuser() {
		if (!motif.trim()) {
			setManque(true);
			return;
		}
		void geste.lancer(() => actions.refuser(offer.id, motif.trim()));
	}

	function basculer(suivant: "publier" | "refuser") {
		setManque(false);
		setMode(suivant);
	}

	return (
		<div className="flex flex-col gap-3 border-t border-mg-border-subtle pt-4">
			{mode === "publier" ? (
				<RangeeDate
					label="Date limite"
					hint="Les étudiants voient l'offre jusqu'à ce jour inclus."
					error={manque && !date ? "Choisissez la date limite de l'offre." : null}
					value={date}
					onChange={setDate}
				>
					<Button
						tone="primary"
						icon={<Send className="size-4" aria-hidden="true" />}
						disabled={geste.enCours}
						onClick={publier}
					>
						Publier
					</Button>
					<Button
						icon={<X className="size-4" aria-hidden="true" />}
						disabled={geste.enCours}
						onClick={() => basculer("refuser")}
					>
						Refuser
					</Button>
				</RangeeDate>
			) : (
				<div className="flex flex-col gap-3">
					<TextAreaInput
						label="Motif du refus"
						hint="L'entreprise le lit dans le mail. Dites-lui quoi corriger."
						error={manque && !motif.trim() ? "Écrivez le motif, l'entreprise le lit." : null}
						required
						max={VERSANT_LIMITES.motif}
						value={motif}
						onChange={setMotif}
					/>
					<div className="flex flex-wrap gap-2">
						<Button tone="primary" disabled={geste.enCours} onClick={refuser}>
							Refuser l'offre
						</Button>
						<Button disabled={geste.enCours} onClick={() => basculer("publier")}>
							Annuler
						</Button>
					</div>
				</div>
			)}
			<GestureError message={geste.echec} />
		</div>
	);
}

/** Une offre publiée ou terminée : sa date, et le moyen de la repousser. */
function EnLigne({ offer, actions }: { offer: EcoleOffer; actions: OffresActions }) {
	const [ouvert, setOuvert] = useState(false);
	const [date, setDate] = useState(offer.expiresOn ?? "");
	const [manque, setManque] = useState(false);
	const geste = useGeste();

	const termine = offer.status === "archivee";
	const quand = offer.expiresOn
		? termine
			? `Terminée le ${formatJour(offer.expiresOn)}.`
			: `En ligne jusqu'au ${formatJour(offer.expiresOn)}.`
		: offer.publishedAt
			? `Publiée le ${formatDate(offer.publishedAt)}.`
			: null;

	async function enregistrer() {
		if (!date) {
			setManque(true);
			return;
		}
		const resultat = await geste.lancer(() => actions.changerDate(offer.id, date));
		if (!resultat) setOuvert(false);
	}

	return (
		<div className="flex flex-col gap-3 border-t border-mg-border-subtle pt-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				{quand ? (
					<p className="t-body flex items-center gap-2 text-mg-body">
						<CalendarDays className="size-4 text-mg-icon-secondary" aria-hidden="true" />
						{quand}
					</p>
				) : null}
				<div className="flex flex-wrap gap-2">
					{termine ? null : (
						<LinkButton to={`/ecole/profils/nouvelle?offre=${encodeURIComponent(offer.id)}`}>
							Envoyer des profils
						</LinkButton>
					)}
					<Button size="sm" onClick={() => setOuvert((valeur) => !valeur)} aria-expanded={ouvert}>
						Changer la date
					</Button>
				</div>
			</div>
			{ouvert ? (
				<RangeeDate
					label="Nouvelle date limite"
					hint={termine ? "Une date à venir remet l'offre en ligne." : undefined}
					error={manque && !date ? "Choisissez la nouvelle date limite." : null}
					value={date}
					onChange={setDate}
				>
					<Button tone="primary" disabled={geste.enCours} onClick={enregistrer}>
						Enregistrer
					</Button>
				</RangeeDate>
			) : null}
			<GestureError message={geste.echec} />
		</div>
	);
}
